import React from "react";
import { Helmet } from "react-helmet";
import { Link } from "react-router-dom";
import Nav from "../components/Nav";
import Footer from "../components/Footer";
import {
  SERVER_TRAINING_CHECKLIST_PATH,
  SERVER_TRAINING_PILLAR_PATH,
} from "../lib/contentClusters";
import "../styles/homepage-redesign.css";

const startFreeHref = "https://app.speakyourmenu.com/signup";
const canonicalUrl = `https://speakyourmenu.com${SERVER_TRAINING_PILLAR_PATH}`;
const checklistUrl = `https://speakyourmenu.com${SERVER_TRAINING_CHECKLIST_PATH}`;
const pageTitle = "Restaurant Server Training Program | Speak Your Menu";
const pageDescription = "A practical server training program for restaurants that need faster onboarding, stronger floor confidence, and more consistent early-shift performance.";

const jumpLinks = [
  { href: "#what-it-is", label: "What it is" },
  { href: "#why-onboarding-fails", label: "Why onboarding fails" },
  { href: "#system", label: "The system" },
  { href: "#first-week", label: "First-week rollout" },
  { href: "#mistakes", label: "Common mistakes" },
  { href: "#faq", label: "FAQ" },
];

const keyTakeaways = [
  "Server training breaks when new hires depend on whoever happens to train them that shift.",
  "A strong server-training program needs clear expectations for day one, repeated reinforcement in the first two weeks, and visible manager follow-through.",
  "The best onboarding system reduces hesitation on the floor before it becomes a guest-facing service problem.",
];

const systemSteps = [
  {
    title: "Define the first-week standard",
    body: "Clarify what new servers must know in their first shifts: menu basics, guest language, allergens, service steps, and escalation points.",
  },
  {
    title: "Move beyond shadowing alone",
    body: "Shadow shifts help, but they are not a complete system. New hires need structured repetition outside the randomness of live service.",
  },
  {
    title: "Coach before confidence drops",
    body: "Managers need a way to spot hesitation early so the right coaching happens before weak knowledge reaches guests.",
  },
  {
    title: "Reinforce what slips first",
    body: "The first gaps usually show up in modifiers, menu descriptions, pacing, and guest questions. The system should reinforce those areas weekly.",
  },
];

const rolloutSteps = [
  "Day 1: Set the first-shift expectations for menu fluency, service flow, and how to ask for help when a new server does not know the answer.",
  "Day 2: Give new hires a short practice loop outside live service so the same menu language appears more than once.",
  "Day 3: Review where hesitation shows up and coach the specific dishes, questions, or pacing issues that keep surfacing.",
  "Day 4: Reinforce with a short pre-shift block so new servers practice retrieval under light pressure before a busy floor does it for them.",
  "Day 5: Check who is progressing, who is still leaning too hard on veterans, and where managers need to intervene.",
  "Day 6: Tighten consistency around guest-facing phrasing, allergy communication, and what great service should sound like at the table.",
  "Day 7: Review the first week and convert the strongest steps into a repeatable onboarding rhythm for the next hire.",
];

const mistakes = [
  "Assuming shadowing equals learning, even though shadow shifts vary wildly by trainer, shift, and table mix.",
  "Overloading new hires with too much information before they have repeated it enough to recall under pressure.",
  "Waiting too long to coach because managers only notice problems once guests do.",
  "Treating server onboarding as complete after week one instead of reinforcing what slips in weeks two and three.",
];

const faqs = [
  {
    question: "How long should server onboarding take?",
    answer: "The first week should create confidence and safe consistency, but reinforcement should continue after that because the highest-pressure knowledge gaps usually appear once service speed increases.",
  },
  {
    question: "What should a server training program cover first?",
    answer: "Start with the guest-facing essentials: what the restaurant serves, how to describe it clearly, how to handle modifiers and allergens, and what to do when the employee does not know the answer yet.",
  },
  {
    question: "Why do new servers still freeze even after training?",
    answer: "Because exposure is not the same as recall. New hires need structured repetition and manager coaching if you want confidence to survive a live shift.",
  },
];

export default function ServerTrainingPage() {
  const breadcrumbSchema = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: [
      { "@type": "ListItem", position: 1, name: "Home", item: "https://speakyourmenu.com/" },
      { "@type": "ListItem", position: 2, name: "Restaurant Server Training Program", item: canonicalUrl },
    ],
  };

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: "Restaurant Server Training Program: A Practical System for Faster Onboarding and Stronger Floor Confidence",
    description: pageDescription,
    author: { "@type": "Organization", name: "Speak Your Menu" },
    publisher: { "@type": "Organization", name: "Speak Your Menu" },
    mainEntityOfPage: canonicalUrl,
    dateModified: new Date().toISOString(),
  };

  return (
    <>
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="description" content={pageDescription} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:site_name" content="Speak Your Menu" />
        <meta property="og:image" content="https://speakyourmenu.com/SpeakYourMenu_Logo.png" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
        <meta name="twitter:image" content="https://speakyourmenu.com/SpeakYourMenu_Logo.png" />
        <link rel="canonical" href={canonicalUrl} />
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      </Helmet>
      <Nav />
      <main id="main-content" tabIndex="-1" className="homepage-redesign resource-page-shell">
        <section className="homepage-section homepage-hero-section resource-hero-section">
          <div className="homepage-hero-bg" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-left" aria-hidden="true" />
          <div className="homepage-hero-glow homepage-hero-glow-right" aria-hidden="true" />
          <div className="homepage-shell">
            <div className="resource-hero-copy">
              <span className="homepage-label-chip">Server Training Systems</span>
              <h1 className="homepage-hero-title resource-hero-title">Restaurant server training that gets new hires confident faster.</h1>
              <p className="homepage-hero-subhead resource-hero-subhead">
                This guide shows restaurant operators how to build a server-training program that sharpens onboarding, reduces first-week hesitation, and gives managers a clearer coaching system.
              </p>
              <div className="homepage-hero-actions resource-hero-actions">
                <a href={startFreeHref} className="homepage-primary-cta">Start free</a>
                <Link to={SERVER_TRAINING_CHECKLIST_PATH} className="resource-secondary-link">Open the onboarding checklist</Link>
              </div>
              <div className="resource-jump-card homepage-surface-card">
                <p className="resource-jump-kicker">In this guide</p>
                <div className="resource-jump-links" aria-label="Jump to sections">
                  {jumpLinks.map((link) => (
                    <a key={link.href} href={link.href}>{link.label}</a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="homepage-section">
          <div className="homepage-shell resource-grid-two">
            <article className="homepage-surface-card resource-card" id="what-it-is">
              <span className="homepage-label-chip">Key takeaways</span>
              <h2 className="homepage-section-title resource-section-title">What a strong server-training program should do</h2>
              <ul className="resource-bullet-list">
                {keyTakeaways.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <aside className="homepage-surface-card resource-card resource-card-accent">
              <span className="homepage-label-chip">Related resource</span>
              <h2 className="homepage-card-title resource-aside-title">Need the shorter version?</h2>
              <p className="homepage-card-copy">Use the onboarding checklist when you want an operator-ready rollout layer for the first week and beyond.</p>
              <Link to={SERVER_TRAINING_CHECKLIST_PATH} className="homepage-primary-cta resource-inline-cta">View checklist</Link>
            </aside>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt" id="why-onboarding-fails">
          <div className="homepage-shell resource-copy-shell">
            <span className="homepage-label-chip">Why onboarding fails</span>
            <h2 className="homepage-section-title resource-section-title">The usual problem is not effort. It is training inconsistency.</h2>
            <p className="homepage-section-body resource-section-body">
              Most restaurant server-training programs lean too heavily on shadowing, verbal downloads, and whoever happens to be leading a shift that day. New hires can survive that system, but they rarely become consistent through it.
            </p>
            <p className="homepage-section-body resource-section-body">
              That inconsistency shows up fast: hesitation at the table, uneven descriptions of the same dish, weak first-week confidence, and a manager team that only notices the issue once service slows down or a guest gets the wrong answer. A stronger system fixes that by making training repeatable and visible.
            </p>
          </div>
        </section>

        <section className="homepage-section" id="system">
          <div className="homepage-shell">
            <span className="homepage-label-chip">The system</span>
            <h2 className="homepage-section-title resource-section-title">A practical four-part system for restaurant server training</h2>
            <div className="resource-step-grid">
              {systemSteps.map((step, index) => (
                <article key={step.title} className="homepage-surface-card resource-step-card">
                  <div className="homepage-step-number">0{index + 1}</div>
                  <h3 className="homepage-card-title">{step.title}</h3>
                  <p className="homepage-card-copy">{step.body}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt" id="first-week">
          <div className="homepage-shell resource-copy-shell">
            <span className="homepage-label-chip">First-week rollout</span>
            <h2 className="homepage-section-title resource-section-title">How to roll the system out in a new server’s first week</h2>
            <ol className="resource-numbered-list">
              {rolloutSteps.map((item) => (
                <li key={item}>{item}</li>
              ))}
            </ol>
          </div>
        </section>

        <section className="homepage-section" id="mistakes">
          <div className="homepage-shell resource-grid-two">
            <article className="homepage-surface-card resource-card">
              <span className="homepage-label-chip">Common mistakes</span>
              <h2 className="homepage-section-title resource-section-title">Four mistakes that keep server training from sticking</h2>
              <ul className="resource-bullet-list">
                {mistakes.map((item) => (
                  <li key={item}>{item}</li>
                ))}
              </ul>
            </article>
            <article className="homepage-surface-card resource-card resource-card-contrast">
              <span className="homepage-label-chip">Next action</span>
              <h2 className="homepage-card-title resource-aside-title">Turn this into a first-week manager checklist</h2>
              <p className="homepage-card-copy">If you want the shorter operating layer, use the onboarding checklist to tighten expectations and coaching for new hires.</p>
              <Link to={SERVER_TRAINING_CHECKLIST_PATH} className="resource-secondary-link resource-secondary-link-inline">Go to checklist</Link>
              <a href={checklistUrl} className="resource-tertiary-link">Share the checklist URL</a>
            </article>
          </div>
        </section>

        <section className="homepage-section homepage-section-alt" id="faq">
          <div className="homepage-shell resource-copy-shell">
            <span className="homepage-label-chip">FAQ</span>
            <h2 className="homepage-section-title resource-section-title">Questions operators ask when rebuilding server onboarding</h2>
            <div className="resource-faq-stack">
              {faqs.map((item) => (
                <article key={item.question} className="homepage-surface-card resource-faq-card">
                  <h3 className="homepage-card-title">{item.question}</h3>
                  <p className="homepage-card-copy">{item.answer}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="homepage-section">
          <div className="homepage-shell homepage-final-cta-shell resource-final-shell">
            <div>
              <span className="homepage-label-chip">Ready to apply it?</span>
              <h2 className="homepage-section-title resource-section-title">See how Speak Your Menu turns better server onboarding into daily practice.</h2>
              <p className="homepage-section-body resource-section-body">
                The platform helps restaurants reinforce menu knowledge, track progress, and coach new hires before hesitation becomes a service problem.
              </p>
            </div>
            <div className="resource-final-actions">
              <a href={startFreeHref} className="homepage-primary-cta">Start free</a>
              <Link to={SERVER_TRAINING_CHECKLIST_PATH} className="resource-secondary-link">Open checklist</Link>
              <a href={checklistUrl} className="resource-tertiary-link">Share the checklist URL</a>
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}